/**
 * The quick filters are the AND part of a filter (`quick`): each control owns the conditions on its
 * own properties and leaves every other condition, and all groups, as they are (design §5.1,
 * addendum A.4.3). "Gewijzigd" works on `last_activity_at` (and, apart, `last_activity_by`);
 * "Laatste contact" on `last_contact_at`, with "Geen contact sinds" as its reverse (A.6).
 */
import { normalizeFilter } from '../query/codec'
import {
  CHANGED_PRESETS,
  matchPreset,
  NO_CONTACT_PRESETS,
  RECENT_CONTACT_PRESETS,
  type DatePreset,
  type DayRange,
} from '../query/dateRanges'
import type { Condition, Filter, Register } from '../query/types'

const CHANGED = 'last_activity_at'
const CHANGED_BY = 'last_activity_by'
const CONTACT = 'last_contact_at'
/** The sort "Gewijzigd" switches to (ED-62): the newest activity first. */
const CHANGED_SORT = '-last_activity_at'

/** The quick conditions on any of these properties. */
export function quickFor(filter: Filter, ...properties: string[]): Condition[] {
  return filter.quick.filter((condition) => properties.includes(condition.property))
}

/** Removes every quick condition on these properties and adds `conditions`; the groups stay. */
export function replaceQuick(filter: Filter, properties: readonly string[], conditions: Condition[]): Filter {
  return normalizeFilter({
    quick: [...filter.quick.filter((condition) => !properties.includes(condition.property)), ...conditions],
    groups: filter.groups,
  })
}

/** The values of the quick condition on `property` with this operator; none when there is no such condition. */
export function listValues(filter: Filter, property: string, operator: string): string[] {
  const condition = filter.quick.find((candidate) => candidate.property === property && candidate.operator === operator)
  return Array.isArray(condition?.value) ? condition.value.map(String) : []
}

/** Sets the list condition of a property; an empty list removes it. */
export function setListFilter(filter: Filter, property: string, operator: string, values: readonly string[]): Filter {
  return replaceQuick(filter, [property], values.length > 0 ? [{ property, operator, value: [...values] }] : [])
}

/** The value of the quick condition on `property` with this operator when it is text. */
export function textValue(filter: Filter, property: string, operator: string): string {
  const condition = filter.quick.find((candidate) => candidate.property === property && candidate.operator === operator)
  return typeof condition?.value === 'string' ? condition.value : ''
}

/** Sets the text condition of a property; blank text removes it. */
export function setTextFilter(filter: Filter, property: string, operator: string, text: string): Filter {
  const value = text.trim()
  return replaceQuick(filter, [property], value ? [{ property, operator, value }] : [])
}

function presetCondition(property: string, preset: DatePreset): Condition {
  const { operator, value } = preset.condition
  return value === undefined ? { property, operator } : { property, operator, value }
}

const isDayPair = (value: unknown): value is [string, string] =>
  Array.isArray(value) && value.length === 2 && value.every((day) => typeof day === 'string')

// ---- Gewijzigd -------------------------------------------------------------------------------

export type ChangedPreset = 'vandaag' | 'week' | '2w' | '30d'

/**
 * What "Gewijzigd" says: a preset or a range of days, and optionally who did it. No preset and no
 * range means no "Gewijzigd" condition at all; `by` alone is ignored then.
 */
export interface ChangedChoice {
  preset?: ChangedPreset
  range?: DayRange
  by: string | null
}

/** Writes "Gewijzigd" (`last_activity_at`) and "Door" (`last_activity_by`) into the quick filters. */
export function setChanged(filter: Filter, choice: ChangedChoice): Filter {
  const preset = choice.preset ? CHANGED_PRESETS.find((candidate) => candidate.key === choice.preset) : undefined
  const conditions: Condition[] = []
  if (preset) conditions.push(presetCondition(CHANGED, preset))
  else if (choice.range) conditions.push({ property: CHANGED, operator: 'between', value: [choice.range.from, choice.range.to] })
  if (conditions.length > 0 && choice.by) conditions.push({ property: CHANGED_BY, operator: 'is_any_of', value: [choice.by] })
  return replaceQuick(filter, [CHANGED, CHANGED_BY], conditions)
}

/** What "Gewijzigd" says now, read back from the filter; a condition that is no preset or range gives neither. */
export function changedChoice(filter: Filter): ChangedChoice {
  const by = listValues(filter, CHANGED_BY, 'is_any_of')[0] ?? null
  const condition = filter.quick.find((candidate) => candidate.property === CHANGED)
  if (!condition) return { by }
  const preset = matchPreset(CHANGED_PRESETS, condition)
  if (preset) return { preset: preset.key as ChangedPreset, by }
  if (condition.operator === 'between' && isDayPair(condition.value)) {
    return { range: { from: condition.value[0], to: condition.value[1] }, by }
  }
  return { by }
}

// ---- Laatste contact -------------------------------------------------------------------------

export type RecentContact = '2w' | '30d'
export type QuietDays = 30 | 60 | 90

/**
 * What "Laatste contact" says: contact within a recent span or a range of days, or "Geen contact
 * sinds" a number of days or a date. For the second kind, relations without any contact count as
 * a hit unless `includeNever` is off (ED-64), which adds `is_known`.
 */
export interface LastContactChoice {
  recent?: RecentContact
  range?: DayRange
  quietDays?: QuietDays
  since?: string
  includeNever: boolean
}

function lastContactCondition(choice: LastContactChoice): Condition | null {
  const preset = choice.recent ? RECENT_CONTACT_PRESETS.find((candidate) => candidate.key === choice.recent) : undefined
  if (preset) return presetCondition(CONTACT, preset)
  if (choice.range) return { property: CONTACT, operator: 'between', value: [choice.range.from, choice.range.to] }
  if (choice.quietDays !== undefined) return { property: CONTACT, operator: 'not_in_last_days', value: choice.quietDays }
  if (choice.since !== undefined) return { property: CONTACT, operator: 'not_since', value: choice.since }
  return null
}

/** Writes "Laatste contact" (`last_contact_at`) into the quick filters. */
export function setLastContact(filter: Filter, choice: LastContactChoice): Filter {
  const main = lastContactCondition(choice)
  const conditions: Condition[] = main ? [main] : []
  const reverse = choice.quietDays !== undefined || choice.since !== undefined
  if (main && reverse && !choice.includeNever) conditions.push({ property: CONTACT, operator: 'is_known' })
  return replaceQuick(filter, [CONTACT], conditions)
}

/** What "Laatste contact" says now, read back from the filter (`includeNever` is on unless `is_known` is there). */
export function lastContactChoice(filter: Filter): LastContactChoice {
  const conditions = quickFor(filter, CONTACT)
  const includeNever = !conditions.some((condition) => condition.operator === 'is_known')
  const main = conditions.find((condition) => condition.operator !== 'is_known')
  if (!main) return { includeNever }
  const recent = matchPreset(RECENT_CONTACT_PRESETS, main)
  if (recent) return { recent: recent.key as RecentContact, includeNever }
  if (matchPreset(NO_CONTACT_PRESETS, main)) return { quietDays: main.value as QuietDays, includeNever }
  if (main.operator === 'between' && isDayPair(main.value)) {
    return { range: { from: main.value[0], to: main.value[1] }, includeNever }
  }
  if (main.operator === 'not_since' && typeof main.value === 'string') return { since: main.value, includeNever }
  return { includeNever }
}

// ---- Periode (Lijsten) -----------------------------------------------------------------------

const PERIOD = 'starts_on'

export type PeriodPreset = 'komend' | 'maand' | 'kwartaal' | 'afgelopen'

/** The periods of the lists overview with the text of the button and the menu. */
export const PERIOD_PRESETS: readonly { key: PeriodPreset; label: string; short: string }[] = [
  { key: 'komend', label: 'Komende 30 dagen', short: 'komende 30 dagen' },
  { key: 'maand', label: 'Deze maand', short: 'deze maand' },
  { key: 'kwartaal', label: 'Dit kwartaal', short: 'dit kwartaal' },
  { key: 'afgelopen', label: 'Afgelopen', short: 'afgelopen' },
]

/** What "Periode" says: a preset or a range of days (on the date of a list); neither means no condition. */
export interface PeriodChoice {
  preset?: PeriodPreset
  range?: DayRange
}

/** Writes "Periode" (`starts_on`) into the quick filters; "Afgelopen" is every date before today. */
export function setPeriod(filter: Filter, choice: PeriodChoice, today: string): Filter {
  let condition: Condition | null = null
  if (choice.preset === 'komend') condition = { property: PERIOD, operator: 'in_next_days', value: 30 }
  else if (choice.preset === 'maand') condition = { property: PERIOD, operator: 'this_month' }
  else if (choice.preset === 'kwartaal') condition = { property: PERIOD, operator: 'this_quarter' }
  else if (choice.preset === 'afgelopen') condition = { property: PERIOD, operator: 'before', value: today }
  else if (choice.range) condition = { property: PERIOD, operator: 'between', value: [choice.range.from, choice.range.to] }
  return replaceQuick(filter, [PERIOD], condition ? [condition] : [])
}

/** What "Periode" says now, read back from the filter; a condition that is no preset or range gives neither. */
export function periodChoice(filter: Filter): PeriodChoice {
  const condition = filter.quick.find((candidate) => candidate.property === PERIOD)
  if (!condition) return {}
  if (condition.operator === 'in_next_days' && condition.value === 30) return { preset: 'komend' }
  if (condition.operator === 'this_month') return { preset: 'maand' }
  if (condition.operator === 'this_quarter') return { preset: 'kwartaal' }
  if (condition.operator === 'before') return { preset: 'afgelopen' }
  if (condition.operator === 'between' && isDayPair(condition.value)) return { range: { from: condition.value[0], to: condition.value[1] } }
  return {}
}

// ---- Sorting ---------------------------------------------------------------------------------

/**
 * The sort to switch to when "Gewijzigd" goes on (ED-62): last activity, newest first. Only when
 * the condition is new, nobody chose a sort (`sortParam` is null) and the view does not sort that
 * way already. Taking "Gewijzigd" off again does not undo it; "Herstellen" does.
 */
export function autoSort(previous: Filter, next: Filter, sortParam: string | null, viewSort: string): string | null {
  const wasOn = quickFor(previous, CHANGED).length > 0
  const isOn = quickFor(next, CHANGED).length > 0
  return isOn && !wasOn && sortParam === null && viewSort !== CHANGED_SORT ? CHANGED_SORT : null
}

/**
 * The sort as the result line says it ("9 contacten · gesorteerd op laatst gewijzigd"); nothing for
 * the sort by name, which is what a list is by default.
 */
export function describeSort(sort: string, register: Register): string | null {
  const key = sort.replace(/^-/, '')
  if (key === 'name') return null
  if (key === CHANGED) return 'laatst gewijzigd'
  return register.properties.find((property) => property.key === key)?.label.toLowerCase() ?? null
}
